import {
  AndroidOutlined,
  AppleOutlined,
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ChromeOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DesktopOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  GlobalOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  MobileOutlined,
  NotificationOutlined,
  PlusOutlined,
  ReloadOutlined,
  RocketOutlined,
  SaveOutlined,
  SendOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Dropdown,
  Form,
  Input,
  List,
  Modal,
  message,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type React from 'react';
import { useEffect, useState } from 'react';
import 'dayjs/locale/hu';
import { useTranslation } from 'react-i18next';
import ScheduledNotifications from '../components/ScheduledNotifications';
import api, { API_URL } from '../config/api';
import { dayjsLocale } from '../i18n/locale';

dayjs.extend(relativeTime);
// The global locale is set in `i18n/config.ts`, from the panel's language.
// Pinning it to `hu` here made the English panel print Hungarian month names.
// The locale data stays imported above: the relative time below asks for the
// panel's own dayjs locale per call, which is `hu` for Hungarian and Serbian.

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface NotificationHistory {
  id: string;
  title: string;
  body: string;
  type: string;
  targetType: string;
  targetId?: string;
  sentBy?: any;
  sentByUser?: any;
  sentAt?: string;
  scheduledFor?: string;
  status: string;
  deliveryStats?: any;
  organization?: any;
  createdAt: string;
  data?: any;
}

interface NotificationStats {
  total: number;
  sent: number;
  failed: number;
  scheduled: number;
  subscribers: number;
}

interface Event {
  id: string;
  title: string;
}

interface Company {
  id: string;
  name: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

const Notifications: React.FC = () => {
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [testForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<NotificationHistory[]>([]);
  const [stats, setStats] = useState<NotificationStats>({
    total: 0,
    sent: 0,
    failed: 0,
    scheduled: 0,
    subscribers: 0,
  });
  const [events, setEvents] = useState<Event[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('history');
  const [refreshing, setRefreshing] = useState(false);
  const [firebaseConfig, setFirebaseConfig] = useState({
    projectId: '',
    clientEmail: '',
    privateKey: '',
  });
  const [configLoading, setConfigLoading] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchStats();
    fetchHistory();
    fetchEvents();
    fetchCompanies();
    fetchUsers();
    fetchFirebaseConfig();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/notifications/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchHistory = async () => {
    setRefreshing(true);
    try {
      const response = await api.get('/notifications/history');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data.data || response.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchFirebaseConfig = async () => {
    try {
      const response = await api.get('/notifications/firebase-config');
      setFirebaseConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch Firebase config:', error);
    }
  };

  const handleSaveFirebaseConfig = async (values: any) => {
    setConfigLoading(true);
    try {
      const response = await api.post('/notifications/firebase-config', values);
      message.success(t('notifications.configSaved'));
      setFirebaseConfig(response.data);
      fetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('notifications.configSaveFailed'));
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSendNotification = async (values: any) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      let endpoint = `${API_URL}/notifications/send`;
      let body: any = {
        title: values.title,
        body: values.body,
        type: values.type,
        targetType: values.targetType,
      };

      // Handle different target types
      if (values.targetType === 'USER' && selectedUsers.length > 0) {
        endpoint = `${API_URL}/notifications/send-bulk`;
        body = {
          title: values.title,
          body: values.body,
          type: values.type,
          userIds: selectedUsers,
        };
      } else if (values.targetType !== 'ALL') {
        body.targetId = values.targetId;
      }

      // Add scheduling if specified
      if (values.scheduledFor) {
        body.scheduledFor = values.scheduledFor.toISOString();
      }

      // Add extra data if provided
      if (values.link) {
        body.data = { link: values.link };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        message.success(t('notifications.sentOk'));
        setSendModalVisible(false);
        form.resetFields();
        setSelectedUsers([]);
        fetchHistory();
        fetchStats();
      } else {
        const error = await response.json();
        message.error(error.message || t('notifications.sendFailed'));
      }
    } catch (error) {
      message.error(t('notifications.sendFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async (values: any) => {
    setLoading(true);
    try {
      const response = await api.post('/notifications/test', values);

      if (response.data.success) {
        message.success(t('notifications.testSentOk'));
        setTestModalVisible(false);
        testForm.resetFields();
      } else {
        message.error(response.data.error || t('notifications.testFailed'));
      }
    } catch (error) {
      message.error(t('notifications.testFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string, scheduledFor?: string) => {
    switch (status) {
      case 'SENT':
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t('notifications.statusSent')}
          </Tag>
        );
      case 'FAILED':
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            {t('notifications.statusFailed')}
          </Tag>
        );
      case 'SCHEDULED': {
        const isOverdue = scheduledFor && dayjs(scheduledFor).isBefore(dayjs());
        return (
          <Tag icon={<ClockCircleOutlined />} color={isOverdue ? 'error' : 'warning'}>
            {isOverdue ? t('notifications.statusOverdue') : t('notifications.statusScheduled')}
          </Tag>
        );
      }
      case 'PENDING':
        return (
          <Tag icon={<ExclamationCircleOutlined />} color="processing">
            {t('notifications.statusPending')}
          </Tag>
        );
      case 'SENDING':
        return (
          <Tag icon={<SendOutlined />} color="processing">
            {t('notifications.statusSending')}
          </Tag>
        );
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getTypeTag = (type: string) => {
    const typeConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      EVENT_REMINDER: {
        color: 'blue',
        icon: <CalendarOutlined />,
        label: t('notifications.typeEventReminder'),
      },
      SESSION_START: {
        color: 'green',
        icon: <ClockCircleOutlined />,
        label: t('notifications.typeSessionStart'),
      },
      ANNOUNCEMENT: {
        color: 'purple',
        icon: <NotificationOutlined />,
        label: t('notifications.typeAnnouncement'),
      },
      TICKET_UPDATE: {
        color: 'orange',
        icon: <BellOutlined />,
        label: t('notifications.typeTicketUpdate'),
      },
      CHAT_MESSAGE: {
        color: 'cyan',
        icon: <MessageOutlined />,
        label: t('notifications.typeChatMessage'),
      },
      SCHEDULE_CHANGE: {
        color: 'red',
        icon: <ExclamationCircleOutlined />,
        label: t('notifications.typeScheduleChange'),
      },
      NETWORKING: {
        color: 'geekblue',
        icon: <TeamOutlined />,
        label: t('notifications.typeNetworking'),
      },
    };

    const config = typeConfig[type] || { color: 'default', icon: null, label: type };
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label}
      </Tag>
    );
  };

  const getTargetTag = (targetType: string, targetId?: string) => {
    const targetConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      ALL: { color: 'green', icon: <GlobalOutlined />, label: t('notifications.targetAll') },
      EVENT: { color: 'blue', icon: <CalendarOutlined />, label: t('notifications.targetEvent') },
      USER: { color: 'purple', icon: <UserOutlined />, label: t('notifications.targetUser') },
      ROLE: { color: 'orange', icon: <TeamOutlined />, label: t('notifications.targetRole') },
      COMPANY: { color: 'cyan', icon: <TeamOutlined />, label: t('notifications.targetCompany') },
    };

    const config = targetConfig[targetType] || { color: 'default', icon: null, label: targetType };
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label}
      </Tag>
    );
  };

  const columns: ColumnsType<NotificationHistory> = [
    {
      title: t('notifications.columnTime'),
      key: 'time',
      width: 150,
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.sentAt
              ? t('notifications.labelSentAt')
              : record.scheduledFor
                ? t('notifications.labelScheduledAt')
                : t('notifications.labelCreatedAt')}
          </Text>
          <Text>
            {dayjs(record.sentAt || record.scheduledFor || record.createdAt).format(
              'YYYY.MM.DD HH:mm'
            )}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(record.sentAt || record.scheduledFor || record.createdAt)
              .locale(dayjsLocale(i18n.language))
              .fromNow()}
          </Text>
        </Space>
      ),
    },
    {
      title: t('common.title'),
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      width: 200,
    },
    {
      title: t('notifications.columnBody'),
      dataIndex: 'body',
      key: 'body',
      ellipsis: true,
      width: 300,
      render: (text) => (
        <Tooltip title={text}>
          <Text ellipsis>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('common.type'),
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => getTypeTag(type),
    },
    {
      title: t('notifications.columnTarget'),
      key: 'target',
      width: 150,
      render: (record) => getTargetTag(record.targetType, record.targetId),
    },
    {
      title: t('notifications.columnStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => getStatusTag(status, record.scheduledFor),
    },
    {
      title: t('notifications.columnStats'),
      key: 'stats',
      width: 150,
      render: (record) => {
        if (!record.deliveryStats) return '-';
        const stats = record.deliveryStats;
        return (
          <Space>
            {stats.successCount !== undefined && (
              <Tag color="success">
                {t('notifications.statsSuccess', { count: stats.successCount })}
              </Tag>
            )}
            {stats.failureCount !== undefined && stats.failureCount > 0 && (
              <Tag color="error">
                {t('notifications.statsFailure', { count: stats.failureCount })}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: t('notifications.columnSender'),
      key: 'sentBy',
      width: 150,
      render: (record) =>
        record.sentByUser ? (
          <Space>
            <Avatar size="small" icon={<UserOutlined />} />
            <Text>
              {record.sentByUser.firstName} {record.sentByUser.lastName}
            </Text>
          </Space>
        ) : (
          '-'
        ),
    },
  ];

  // Import MessageOutlined
  const MessageOutlined = () => <span>💬</span>;

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card>
            <Row align="middle" justify="space-between">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  <BellOutlined /> {t('notifications.pageTitle')}
                </Title>
              </Col>
              <Col>
                <Space>
                  <Button
                    icon={<SendOutlined />}
                    type="primary"
                    size="large"
                    onClick={() => setSendModalVisible(true)}
                  >
                    {t('notifications.newNotification')}
                  </Button>
                  <Button
                    icon={<RocketOutlined />}
                    size="large"
                    onClick={() => setTestModalVisible(true)}
                  >
                    {t('notifications.sendTest')}
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    size="large"
                    onClick={() => {
                      fetchStats();
                      fetchHistory();
                    }}
                  >
                    {t('notifications.refresh')}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title={t('notifications.statTotal')}
              value={stats.total}
              prefix={<BellOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title={t('notifications.statSent')}
              value={stats.sent}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title={t('notifications.statusFailed')}
              value={stats.failed}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title={t('notifications.statusScheduled')}
              value={stats.scheduled}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title={t('notifications.statSubscribers')}
              value={stats.subscribers}
              valueStyle={{ color: '#1890ff' }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'history',
              label: (
                <span>
                  <HistoryOutlined /> {t('notifications.tabHistory')}
                </span>
              ),
              children: (
                <Table
                  columns={columns}
                  dataSource={notifications}
                  rowKey="id"
                  loading={refreshing}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => t('notifications.paginationTotal', { count: total }),
                  }}
                  scroll={{ x: 1200 }}
                />
              ),
            },
            {
              key: 'scheduled',
              label: (
                <span>
                  <ClockCircleOutlined /> {t('notifications.tabScheduled')}
                </span>
              ),
              children: <ScheduledNotifications />,
            },
            {
              key: 'config',
              label: (
                <span>
                  <SettingOutlined /> {t('notifications.tabConfig')}
                </span>
              ),
              children: (
                <>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Alert
                        message={t('notifications.fbAlertTitle')}
                        description={
                          <div>
                            <p>{t('notifications.fbAlertIntro')}</p>
                            <p style={{ marginTop: 8 }}>
                              <strong>{t('notifications.fbAlertHowTo')}</strong>
                            </p>
                            <ol style={{ marginLeft: 20, marginTop: 8 }}>
                              <li>
                                {t('notifications.fbStep1Before')}
                                <a
                                  href="https://console.firebase.google.com"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t('notifications.fbStep1Link')}
                                </a>
                                {t('notifications.fbStep1After')}
                              </li>
                              <li>{t('notifications.fbStep2')}</li>
                              <li>{t('notifications.fbStep3')}</li>
                              <li>{t('notifications.fbStep4')}</li>
                              <li>{t('notifications.fbStep5')}</li>
                            </ol>
                          </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                      />
                    </Col>
                  </Row>

                  <Form
                    layout="vertical"
                    initialValues={firebaseConfig}
                    onFinish={handleSaveFirebaseConfig}
                    style={{ maxWidth: 800 }}
                  >
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          name="projectId"
                          label={
                            <span>
                              {t('notifications.projectIdLabel')}{' '}
                              <Tooltip title={t('notifications.projectIdTooltip')}>
                                <InfoCircleOutlined style={{ marginLeft: 4 }} />
                              </Tooltip>
                            </span>
                          }
                          rules={[
                            { required: true, message: t('notifications.projectIdRequired') },
                          ]}
                        >
                          <Input
                            placeholder={t('notifications.projectIdPlaceholder')}
                            size="large"
                            prefix={<KeyOutlined />}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          name="clientEmail"
                          label={
                            <span>
                              {t('notifications.clientEmailLabel')}{' '}
                              <Tooltip title={t('notifications.clientEmailTooltip')}>
                                <InfoCircleOutlined style={{ marginLeft: 4 }} />
                              </Tooltip>
                            </span>
                          }
                          rules={[
                            { required: true, message: t('notifications.clientEmailRequired') },
                            { type: 'email', message: t('notifications.clientEmailInvalid') },
                          ]}
                        >
                          <Input
                            placeholder={t('notifications.clientEmailPlaceholder')}
                            size="large"
                            prefix={<UserOutlined />}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          name="privateKey"
                          label={
                            <span>
                              {t('notifications.privateKeyLabel')}{' '}
                              <Tooltip title={t('notifications.privateKeyTooltip')}>
                                <InfoCircleOutlined style={{ marginLeft: 4 }} />
                              </Tooltip>
                            </span>
                          }
                          rules={[
                            { required: true, message: t('notifications.privateKeyRequired') },
                          ]}
                        >
                          <TextArea
                            placeholder={t('notifications.privateKeyPlaceholder')}
                            rows={10}
                            style={{ fontFamily: 'monospace', fontSize: 12 }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {!firebaseConfig.projectId && (
                      <Alert
                        message={t('notifications.configMissingTitle')}
                        description={t('notifications.configMissingBody')}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    )}

                    {firebaseConfig.projectId && (
                      <Alert
                        message={t('notifications.configActiveTitle')}
                        description={t('notifications.configActiveBody', {
                          project: firebaseConfig.projectId,
                        })}
                        type="success"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    )}

                    <Form.Item>
                      <Space>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={configLoading}
                          icon={<SaveOutlined />}
                          size="large"
                        >
                          {t('notifications.saveConfig')}
                        </Button>
                        <Button
                          onClick={() => {
                            fetchStats();
                            fetchFirebaseConfig();
                          }}
                          icon={<ReloadOutlined />}
                          size="large"
                        >
                          {t('notifications.refresh')}
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>

                  <Divider />

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Title level={4}>{t('notifications.testingTitle')}</Title>
                      <Text type="secondary">{t('notifications.testingHint')}</Text>
                    </Col>
                    <Col span={24}>
                      <Space>
                        <Button
                          icon={<RocketOutlined />}
                          onClick={() => setTestModalVisible(true)}
                          disabled={!firebaseConfig.projectId}
                        >
                          {t('notifications.sendTestNotification')}
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* Send Notification Modal */}
      <Modal
        title={
          <>
            <SendOutlined /> {t('notifications.newNotification')}
          </>
        }
        open={sendModalVisible}
        onCancel={() => {
          setSendModalVisible(false);
          form.resetFields();
          setSelectedUsers([]);
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSendNotification}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label={t('notifications.typeLabel')}
                rules={[{ required: true, message: t('notifications.selectTypeRequired') }]}
              >
                <Select placeholder={t('notifications.selectTypePlaceholder')} size="large">
                  <Option value="ANNOUNCEMENT">
                    <NotificationOutlined /> {t('notifications.typeAnnouncement')}
                  </Option>
                  <Option value="EVENT_REMINDER">
                    <CalendarOutlined /> {t('notifications.typeEventReminder')}
                  </Option>
                  <Option value="SESSION_START">
                    <ClockCircleOutlined /> {t('notifications.typeSessionStart')}
                  </Option>
                  <Option value="SCHEDULE_CHANGE">
                    <ExclamationCircleOutlined /> {t('notifications.typeScheduleChange')}
                  </Option>
                  <Option value="TICKET_UPDATE">
                    <BellOutlined /> {t('notifications.typeTicketUpdate')}
                  </Option>
                  <Option value="NETWORKING">
                    <TeamOutlined /> {t('notifications.typeNetworking')}
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="targetType"
                label={t('notifications.columnTarget')}
                rules={[{ required: true, message: t('notifications.selectTargetRequired') }]}
              >
                <Select
                  placeholder={t('notifications.selectTargetPlaceholder')}
                  size="large"
                  onChange={(value) => {
                    if (value !== 'USER') {
                      setSelectedUsers([]);
                    }
                  }}
                >
                  <Option value="ALL">
                    <GlobalOutlined /> {t('notifications.targetAllOption')}
                  </Option>
                  <Option value="EVENT">
                    <CalendarOutlined /> {t('notifications.targetEvent')}
                  </Option>
                  <Option value="USER">
                    <UserOutlined /> {t('notifications.targetUserOption')}
                  </Option>
                  <Option value="COMPANY">
                    <TeamOutlined /> {t('notifications.targetCompanyOption')}
                  </Option>
                  <Option value="ROLE">
                    <TeamOutlined /> {t('notifications.targetRoleOption')}
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.targetType !== currentValues.targetType
            }
          >
            {({ getFieldValue }) => {
              const targetType = getFieldValue('targetType');

              if (targetType === 'EVENT') {
                return (
                  <Form.Item
                    name="targetId"
                    label={t('common.selectEvent')}
                    rules={[{ required: true, message: t('common.selectEventRequired') }]}
                  >
                    <Select placeholder={t('common.selectEvent')} size="large" showSearch>
                      {events.map((event) => (
                        <Option key={event.id} value={event.id}>
                          {event.title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              } else if (targetType === 'USER') {
                return (
                  <Form.Item label={t('notifications.selectUsers')} required>
                    <Select
                      mode="multiple"
                      placeholder={t('notifications.selectUsers')}
                      size="large"
                      value={selectedUsers}
                      onChange={setSelectedUsers}
                      filterOption={(input, option) =>
                        (option?.children as any)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      showSearch
                    >
                      {users.map((user) => (
                        <Option key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} ({user.email})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              } else if (targetType === 'COMPANY') {
                return (
                  <Form.Item
                    name="targetId"
                    label={t('notifications.selectCompany')}
                    rules={[{ required: true, message: t('notifications.selectCompanyRequired') }]}
                  >
                    <Select placeholder={t('notifications.selectCompany')} size="large" showSearch>
                      {companies.map((company) => (
                        <Option key={company.id} value={company.id}>
                          {company.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              } else if (targetType === 'ROLE') {
                return (
                  <Form.Item
                    name="targetId"
                    label={t('notifications.selectRole')}
                    rules={[{ required: true, message: t('notifications.selectRoleRequired') }]}
                  >
                    <Select placeholder={t('notifications.selectRole')} size="large">
                      <Option value="SUPER_ADMIN">{t('notifications.roleSuperAdmin')}</Option>
                      <Option value="ORGANIZATION_ADMIN">
                        {t('notifications.roleOrganizationAdmin')}
                      </Option>
                      <Option value="EVENT_ADMIN">{t('notifications.roleEventAdmin')}</Option>
                      <Option value="SPEAKER">{t('notifications.roleSpeaker')}</Option>
                      <Option value="ATTENDEE">{t('notifications.roleAttendee')}</Option>
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="title"
            label={t('notifications.titleLabel')}
            rules={[{ required: true, message: t('notifications.titleRequired') }]}
          >
            <Input
              placeholder={t('notifications.titlePlaceholder')}
              size="large"
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="body"
            label={t('notifications.bodyLabel')}
            rules={[{ required: true, message: t('notifications.bodyRequired') }]}
          >
            <TextArea
              placeholder={t('notifications.bodyPlaceholder')}
              rows={4}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="link" label={t('notifications.linkLabel')}>
                <Input
                  placeholder={t('notifications.linkPlaceholder')}
                  size="large"
                  prefix={<GlobalOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="scheduledFor"
                label={
                  <span>
                    <ClockCircleOutlined /> {t('notifications.scheduleLabel')}
                  </span>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <DatePicker
                    showTime={{
                      format: 'HH:mm',
                      minuteStep: 5,
                    }}
                    format="YYYY-MM-DD HH:mm"
                    size="large"
                    placeholder={t('notifications.schedulePlaceholder')}
                    style={{ width: '100%' }}
                    disabledDate={(current) => {
                      return current && current < dayjs().startOf('day');
                    }}
                    showNow={false}
                  />
                  <Space wrap>
                    <Button
                      size="small"
                      onClick={() => {
                        form.setFieldsValue({
                          scheduledFor: dayjs().add(1, 'hour'),
                        });
                      }}
                    >
                      {t('notifications.plus1Hour')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        form.setFieldsValue({
                          scheduledFor: dayjs().add(3, 'hour'),
                        });
                      }}
                    >
                      {t('notifications.plus3Hours')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        form.setFieldsValue({
                          scheduledFor: dayjs().add(1, 'day').hour(9).minute(0),
                        });
                      }}
                    >
                      {t('notifications.tomorrow9')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        form.setFieldsValue({
                          scheduledFor: dayjs().add(1, 'day').hour(14).minute(0),
                        });
                      }}
                    >
                      {t('notifications.tomorrow14')}
                    </Button>
                    <Button
                      size="small"
                      type="text"
                      danger
                      onClick={() => {
                        form.setFieldsValue({
                          scheduledFor: null,
                        });
                      }}
                    >
                      {t('notifications.clearSchedule')}
                    </Button>
                  </Space>
                </Space>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.scheduledFor !== currentValues.scheduledFor
            }
          >
            {({ getFieldValue }) => {
              const scheduledFor = getFieldValue('scheduledFor');
              const isScheduled = scheduledFor && dayjs(scheduledFor).isAfter(dayjs());

              return (
                <Alert
                  message={
                    isScheduled
                      ? t('notifications.alertScheduledTitle')
                      : t('notifications.alertImmediateTitle')
                  }
                  description={
                    isScheduled
                      ? t('notifications.alertScheduledBody', {
                          time: dayjs(scheduledFor).format('YYYY.MM.DD HH:mm'),
                        })
                      : t('notifications.alertImmediateBody')
                  }
                  type={isScheduled ? 'warning' : 'info'}
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            }}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setSendModalVisible(false);
                  form.resetFields();
                  setSelectedUsers([]);
                }}
              >
                {t('notifications.cancel')}
              </Button>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.scheduledFor !== currentValues.scheduledFor
                }
              >
                {({ getFieldValue }) => {
                  const scheduledFor = getFieldValue('scheduledFor');
                  const isScheduled = scheduledFor && dayjs(scheduledFor).isAfter(dayjs());

                  return (
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      icon={isScheduled ? <ClockCircleOutlined /> : <SendOutlined />}
                    >
                      {isScheduled
                        ? t('notifications.submitScheduled', {
                            time: dayjs(scheduledFor).format('MM.DD HH:mm'),
                          })
                        : t('notifications.submitSend')}
                    </Button>
                  );
                }}
              </Form.Item>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Test Notification Modal */}
      <Modal
        title={
          <>
            <RocketOutlined /> {t('notifications.sendTestNotification')}
          </>
        }
        open={testModalVisible}
        onCancel={() => {
          setTestModalVisible(false);
          testForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message={t('notifications.testAlertTitle')}
          description={t('notifications.testAlertBody')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={testForm} layout="vertical" onFinish={handleTestNotification}>
          <Form.Item
            name="fcmToken"
            label={t('notifications.fcmTokenLabel')}
            rules={[{ required: true, message: t('notifications.fcmTokenRequired') }]}
          >
            <TextArea placeholder={t('notifications.fcmTokenPlaceholder')} rows={3} />
          </Form.Item>

          <Form.Item
            name="title"
            label={t('notifications.testTitleLabel')}
            rules={[{ required: true, message: t('notifications.titleRequired') }]}
          >
            <Input placeholder={t('notifications.testAlertTitle')} size="large" />
          </Form.Item>

          <Form.Item
            name="body"
            label={t('notifications.testBodyLabel')}
            rules={[{ required: true, message: t('notifications.bodyRequired') }]}
          >
            <TextArea placeholder={t('notifications.testBodyPlaceholder')} rows={3} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setTestModalVisible(false);
                  testForm.resetFields();
                }}
              >
                {t('notifications.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} icon={<RocketOutlined />}>
                {t('notifications.sendTest')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Notifications;
